<script lang="ts" setup>
import type { AddressSuggestionType } from '~/api/common';
import CommonAPI from '~/api/common';

const props = defineProps<{
  searchPrefix?: string;
  disabled?: boolean;
  query?: string;
  preventClosing?: (value: AddressSuggestionType) => boolean;
  dataTestId?: string;
}>();
const emit = defineEmits<{
  (event: 'update:query', value: string): void;
  (event: 'select', value: AddressSuggestionType): void;
}>();

const { t } = useI18n();

const localQuery = ref(props.query || '');
const timerId = ref<NodeJS.Timeout>();
const suggestions = ref<AddressSuggestionType[]>([]);

const { execute, isValid, isLoading } = useRequest(
  CommonAPI.suggestAddress,
  () => ({ query: { q: `${props.searchPrefix || ''} ${localQuery.value}` } }),
);

const getOptionKey = (value: AddressSuggestionType) => value.unrestrictedValue;

const getOptionLabel = (value: AddressSuggestionType) =>
  [
    value.data.settlementWithType,
    value.data.streetWithType,
    [
      value.data.house && `${value.data.houseType} ${value.data.house}`,
      value.data.block && `${value.data.blockType} ${value.data.block}`,
    ]
      .filter(Boolean)
      .join(' '),
    value.data.flat && `${value.data.flatType} ${value.data.flat}`,
  ]
    .filter(Boolean)
    .join(', ');

const getOptionHint = (value: AddressSuggestionType) =>
  Array.from(
    new Set([
      value.data.regionWithType,
      value.data.areaWithType,
      value.data.cityWithType,
    ]),
  )
    .filter(Boolean)
    .join(', ');

const onSelect = (value: AddressSuggestionType) => {
  localQuery.value = getOptionLabel(value);
  emit('select', value);
};

const load = () =>
  execute()?.then((data) => {
    suggestions.value = data.suggestions;
  });

watch(localQuery, (v) => {
  if (isValid.value) {
    clearTimeout(timerId.value);
    if (v) {
      timerId.value = setTimeout(() => load(), 300);
    }
  } else suggestions.value = [];

  emit('update:query', v);
});

watch(
  () => props.query,
  (v) => {
    if (v !== undefined) localQuery.value = v;
  },
);

onMounted(() => {
  if (isValid.value && localQuery.value) load();
});
</script>

<template>
  <BaseSearchSelect
    v-model:query="localQuery"
    @select="onSelect"
    :options="suggestions"
    :get-option-key="getOptionKey"
    :get-option-label="getOptionLabel"
    :loading="isLoading"
    :prevent-closing="preventClosing"
    :disabled="disabled"
    :placeholder="t('placeholder')"
    :data-test-id="dataTestId"
  >
    <template #label="{ option }">
      {{ getOptionLabel(option) }}
    </template>
    <template #hint="{ option }">
      {{ getOptionHint(option) }}
    </template>
  </BaseSearchSelect>
</template>

<i18n>
ru:
  placeholder: Адрес

en:
  placeholder: Address
</i18n>
